"use client";

import { VidMuseEvent } from "@/common/events";
import { logger } from "@/common/logger";
import { MobileWarningModal } from "@/component/MobileWarningModal";
import { OnboardingModal } from "@/component/OnboardingModal";
import { useDeviceDetection } from "@/hooks/useDeviceDetection";
import { useToolConfig } from "@/hooks/useToolConfig";

import { setupChunkErrorHandler } from "@/util/chunkErrorHandler";
import {
  safeLocalStorageGetItem,
  safeLocalStorageSetItem,
} from "@/util/safeLocalStorage";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { useEffect, useState } from "react";

const ONBOARDING_COMPLETED_KEY = "vidmuse_onboarding_completed";

export function Init() {
  const [showMobileModal, setShowMobileModal] = useState(false);
  const [showOnboardingModal, setShowOnboardingModal] = useState(false);
  const { isMobile } = useDeviceDetection();
  const router = useRouter();

  useToolConfig();

  useEffect(() => {
    logger.init();
    // Setup chunk error handler to handle loading failures
    setupChunkErrorHandler();
  }, []);

  const pathname = usePathname();
  const searchParams = useSearchParams();

  useEffect(() => {
    if (!pathname) return;

    // Map pathname to page exposure event
    let event: VidMuseEvent | null = null;
    if (pathname === "/") {
      event = VidMuseEvent.HomePageDisplayed;
    } else if (pathname.startsWith("/login")) {
      event = VidMuseEvent.LoginPageDisplayed;
    } else if (pathname === "/thread") {
      event = VidMuseEvent.ThreadListPageDisplayed;
    } else if (pathname.startsWith("/share/")) {
      event = VidMuseEvent.ThreadSharePageDisplayed;
    } else if (pathname.startsWith("/thread/")) {
      event = VidMuseEvent.ThreadDetailPageDisplayed;
    } else if (pathname === "/pricing") {
      event = VidMuseEvent.PricingPageDisplayed;
    }

    if (event) {
      logger.event(event);
    }
  }, [pathname]);

  useEffect(() => {
    // Check for paySuccess parameter in URL on home page
    if (pathname === "/") {
      const paySuccess = searchParams?.get("paySuccess");
      const payValue = searchParams?.get("payValue");
      const isNewUser = searchParams?.get("newUser");
      const loginSuccess = searchParams?.get("loginSuccess");

      const params = new URLSearchParams(searchParams?.toString());
      let shouldUpdateUrl = false;

      if (paySuccess === "true") {
        logger.event(VidMuseEvent.Purchase, {
          currency: "USD",
          value: payValue,
        });

        params.delete("paySuccess");
        params.delete("payValue");
        shouldUpdateUrl = true;
      }

      if (isNewUser === "true" && loginSuccess === "true") {
        const loginFrom = searchParams?.get("loginFrom");
        logger.event(VidMuseEvent.NewUserJoin, {
          value: {
            loginFrom,
          },
        });

        params.delete("newUser");
        params.delete("loginSuccess");
        params.delete("loginFrom");
        shouldUpdateUrl = true;

        // Show onboarding modal if not completed before
        const hasCompletedOnboarding =
          safeLocalStorageGetItem(ONBOARDING_COMPLETED_KEY) === "true";
        if (!hasCompletedOnboarding) {
          setShowOnboardingModal(true);
        }
      }

      if (shouldUpdateUrl) {
        const newQuery = params.toString();
        router.replace(`${pathname}${newQuery ? `?${newQuery}` : ""}`);
      }
    }
  }, [pathname, searchParams, router]);

  useEffect(() => {
    if (isMobile) {
      setShowMobileModal(true);
    }
  }, [isMobile]);

  const handleClose = () => {
    setShowMobileModal(false);
  };

  const handleOnboardingComplete = () => {
    // Mark as completed in localStorage
    safeLocalStorageSetItem(ONBOARDING_COMPLETED_KEY, "true");

    // Close modal (always close, even if storage fails)
    setShowOnboardingModal(false);
  };

  if (pathname?.startsWith("/thread/")) {
    return <MobileWarningModal open={showMobileModal} onClose={handleClose} />;
  }

  return (
    <>
      <OnboardingModal
        open={showOnboardingModal}
        onComplete={handleOnboardingComplete}
      />
    </>
  );
}
